import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Edit, Trash2, Eye, Save, X, FileText, Globe, Clock, ShieldCheck
} from 'lucide-react';
import RichTextEditor from '../cms/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PageManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    meta_title: '',
    meta_description: '',
    status: 'draft',
    template: 'default'
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/cms-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data);
    } catch (error) {
      console.error('Failed to load pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentPage(null);
    setFormData({
      title: '',
      slug: '',
      content: '<p>Start writing your content here...</p>',
      meta_title: '',
      meta_description: '',
      status: 'draft',
      template: 'default'
    });
    setEditing(true);
  };

  const handleEdit = (page) => {
    setCurrentPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content,
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      status: page.status,
      template: page.template
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!formData.title || !formData.slug) {
        toast.error('Title and slug are required');
        return;
      }

      if (currentPage) {
        // Update existing page
        await axios.put(
          `${API_URL}/api/cms-pages/${currentPage.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Page updated successfully!');
      } else {
        // Create new page
        await axios.post(
          `${API_URL}/api/cms-pages`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Page created successfully!');
      }

      setEditing(false);
      setCurrentPage(null);
      loadPages();
    } catch (error) {
      console.error('Failed to save page:', error);
      toast.error(error.response?.data?.detail || 'Failed to save page');
    }
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/cms-pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page deleted successfully');
      loadPages();
    } catch (error) {
      console.error('Failed to delete page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handleMigratePages = async () => {
    if (!window.confirm('Migrate existing pages to CMS? This will import all old pages into the new CMS system.')) {
      return;
    }

    try {
      setMigrating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/cms-migration/migrate-pages`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `Migration completed! Migrated: ${response.data.migrated}, Skipped: ${response.data.skipped}`
      );
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Migration errors:', response.data.errors);
      }
      
      loadPages();
    } catch (error) {
      console.error('Failed to migrate pages:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate pages');
    } finally {
      setMigrating(false);
    }
  };

  const handleSeedEssentialPages = async () => {
    if (!window.confirm('Create essential HIPAA and BAA pages? This will add HIPAA Compliance and Business Associate Agreement pages if they do not exist.')) {
      return;
    }

    try {
      setSeeding(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/cms-pages/admin/seed-essential-pages`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.seeded_pages.length > 0) {
        toast.success(
          `Pages created successfully: ${response.data.seeded_pages.join(', ')}`
        );
      } else {
        toast.info('All essential pages already exist');
      }
      
      loadPages();
    } catch (error) {
      console.error('Failed to seed essential pages:', error);
      toast.error(error.response?.data?.detail || 'Failed to create essential pages');
    } finally {
      setSeeding(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  if (editing) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {currentPage ? 'Edit Page' : 'Create New Page'}
            </h2>
            <p className="text-gray-600">Build beautiful content with our rich text editor</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setEditing(false);
                setCurrentPage(null);
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Page
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Page Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Page Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (!currentPage) {
                      setFormData({ ...formData, title: e.target.value, slug: generateSlug(e.target.value) });
                    }
                  }}
                  placeholder="About Us"
                />
              </div>
              <div className="space-y-2">
                <Label>URL Slug *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="about-us"
                />
                <p className="text-xs text-gray-500">URL: /{formData.slug}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Template</Label>
                <select
                  value={formData.template}
                  onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="default">Default</option>
                  <option value="full-width">Full Width</option>
                  <option value="landing">Landing Page</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Meta Title (SEO)</Label>
              <Input
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                placeholder="Leave empty to use page title"
              />
            </div>

            <div className="space-y-2">
              <Label>Meta Description (SEO)</Label>
              <Input
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="Brief description for search engines"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Page Content</CardTitle>
            <CardDescription>
              Use the rich text editor below to create your page content
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RichTextEditor
              content={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">CMS Pages</h2>
          <p className="text-gray-600">Manage all your website pages in one place</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleSeedEssentialPages}
            variant="outline"
            disabled={seeding}
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
            data-testid="seed-essential-pages-btn"
          >
            {seeding ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600 mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4 mr-2" />
                Add HIPAA/BAA Pages
              </>
            )}
          </Button>
          <Button
            onClick={handleMigratePages}
            variant="outline"
            disabled={migrating}
          >
            {migrating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600 mr-2"></div>
                Migrating...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Import Old Pages
              </>
            )}
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New Page
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      ) : pages.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pages Yet</h3>
              <p className="text-gray-600 mb-6">
                Create your first page to get started with your CMS
              </p>
              <Button
                onClick={handleCreate}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Page
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {pages.map((page) => (
            <Card key={page.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{page.title}</h3>
                      <Badge
                        className={
                          page.status === 'published'
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                        }
                      >
                        {page.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Globe className="h-4 w-4" />
                        <span>/{page.slug}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>Updated {new Date(page.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {page.meta_description && (
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {page.meta_description}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {page.status === 'published' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const currentOrigin = window.location.origin;
                          // All CMS pages now use friendly URLs directly after domain
                          const pageUrl = `${currentOrigin}/${page.slug}`;
                          window.open(pageUrl, '_blank');
                        }}
                        title="View Page"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(page)}
                      title="Edit Page"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(page.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete Page"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PageManager;